import React, { useMemo, Fragment, useState, useCallback } from 'react';
import theme from './ChangeRequestOverviewContractorItem.module.scss';
import { SimpleCard } from '@/components/Cards';
import { ChangeRequestOverview, ChangeRequestUserOverview } from '@/types';
import { UserIcon } from '@/components/Icons';
import { Trans } from '@lingui/react/macro';
import { ProjectRelativeLink } from '@/components/navigation';
import Indicator, { IndicatorColor } from '@/components/Indicator';
import { classnames } from '@/utils/html';
import { formatDistance, addSeconds } from 'date-fns';
import type { Locale } from 'date-fns/locale';
import { useForceUpdate, useInterval, useRequestLanguage } from '@/utils/hooks';

interface AverageTimeDisplayProps {
    averageInSeconds: number;
}
const AverageTimeDisplay: React.FC<AverageTimeDisplayProps> = ({ averageInSeconds }) => {
    const [locale, setLocale] = useState<Locale | null>(null);

    useRequestLanguage(useCallback((name, obj) => setLocale(obj), []));
    const forceUpdate = useForceUpdate();

    useInterval(() => {
        forceUpdate();
    }, 5000);

    if (!locale) {
        return null;
    }

    const startDate = Date.now();
    const endDate = addSeconds(Date.now(), averageInSeconds);
    return <>{formatDistance(startDate, endDate, { locale })}</>;
};

interface ChangeRequestOverviewContractorItemProps {
    userOverview: ChangeRequestUserOverview;
    changeRequests: ChangeRequestOverview[];
}
const ChangeRequestOverviewContractorItem: React.FC<ChangeRequestOverviewContractorItemProps> = ({
    userOverview,
    changeRequests,
}) => {
    const userParticipants = useMemo(
        () => changeRequests.flatMap((cr) => cr.participants.find((p) => p.userId === userOverview.userId)),
        [changeRequests, userOverview]
    );
    if (changeRequests.length === 0) return null;

    const firstUserParticipant = userParticipants[0]!;

    const renderStatus = (changRequest: ChangeRequestOverview, isLast: boolean) => {
        const sentOffer = changRequest.participants.find((p) => p.userId === userOverview.userId)?.sentOffer ?? null;

        let label = <Trans>No offer</Trans>;
        let color: IndicatorColor = 'danger';

        if (sentOffer) {
            label = <Trans>Offer sent</Trans>;
            color = 'success';
        }
        return (
            <Indicator className={classnames(theme.indicator, { [theme.last]: isLast })} color={color}>
                {label}
            </Indicator>
        );
    };
    return (
        <>
            <SimpleCard>
                <div className={theme.contractor}>
                    <div className={theme.userInfo}>
                        <UserIcon sizeInPx={32} userId={userOverview.userId} />
                        {firstUserParticipant.displayName}
                        <span>{firstUserParticipant.profileName}</span>
                    </div>
                    <div className={theme.info}>
                        <span>
                            <Trans>Unsubmitted offers</Trans>
                        </span>
                        {userOverview.unsubmittedOffers}
                    </div>
                    <div className={theme.info}>
                        <span>
                            <Trans>Time to submit offer</Trans>
                        </span>
                        {userOverview.averageTimeToSendInSeconds ? (
                            <AverageTimeDisplay averageInSeconds={userOverview.averageTimeToSendInSeconds} />
                        ) : (
                            <Trans>N/A</Trans>
                        )}
                    </div>
                </div>

                <div className={theme.changeRequestContainer}>
                    {changeRequests.map((cr, index) => (
                        <Fragment key={cr.changeRequestId}>
                            <div
                                className={classnames(theme.changeRequestRow, {
                                    [theme.last]: index === changeRequests.length - 1,
                                })}>
                                <ProjectRelativeLink to={`/residential-units/${cr.residentialUnitId}`}>
                                    {cr.residentialUnitName}
                                </ProjectRelativeLink>
                                <ProjectRelativeLink className={theme.requestLink} to={`/residential-units/${cr.residentialUnitId}/changes/${cr.changeRequestId}`}>
                                    {cr.subject}
                                </ProjectRelativeLink>
                                <small>{cr.description}</small>
                                {renderStatus(cr, index === changeRequests.length - 1)}
                            </div>
                            {index !== changeRequests.length - 1 && <div className={theme.border} />}
                        </Fragment>
                    ))}
                </div>
            </SimpleCard>
        </>
    );
};

export default ChangeRequestOverviewContractorItem;
